/**
 * Labels for Knowledge Agent action bucket pages / media (`KAActionBucketPageDataLabel` — API).
 */

export const KAActionBucketPageDataLabelValues = Object.freeze({
  KnowledgeAgent: 'knowledgeBase',
  PersonalizationTool_Registrant_Sessions: 'personalizationTool/registrants/sessions',
  PersonalizationTool_Registrant_Exhibitors: 'personalizationTool/registrants/exhibitors',
  PersonalizationTool_Registrant_Passes: 'personalizationTool/registrants/passes',
  PersonalizationTool_Registrant_Experiences: 'personalizationTool/registrants/experiences',
  PersonalizationTool_Registrant_Speakers: 'personalizationTool/registrants/speakers',
  PersonalizationTool_Events_EventInfo: 'personalizationTool/events/event_info',
  PersonalizationTool_Sponsors_SponsorshipPackages:
    'personalizationTool/sponsors/sponsorship_packages',
  PersonalizationTool_Sponsors_MatchedAudiences: 'personalizationTool/sponsors/matched_audiences',
  PersonalizationTool_Awards_AwardEntrants: 'personalizationTool/awards/awards',
  PersonalizationTool_Awards_AwardJudges: 'personalizationTool/awards/award_judges',
  PersonalizationTool_Awards_AwardKeyEvents: 'personalizationTool/awards/award_keyevents',
  /** Legacy — existing indexed items may still use this until rewritten. */
  PersonalizationTool_Awards_AwardKeyDates: 'personalizationTool/awards/award_keydates',
  PersonalizationTool_Awards_AwardJudgeProcess: 'personalizationTool/awards/award_judging_process',
  PersonalizationTool_Awards_AwardPreviousWinner:
    'personalizationTool/awards/award_previous_winners',
});

/** All allowed label strings (frozen list for validation) */
export const KA_ACTION_BUCKET_PAGE_LABEL_VALUES = Object.freeze(
  Object.values(KAActionBucketPageDataLabelValues)
);

const LABEL_VALUE_SET = new Set(KA_ACTION_BUCKET_PAGE_LABEL_VALUES);

/** A team's own label is stored on items, collections and `requiredFields` as `custom/<labelId>`. */
export const CUSTOM_LABEL_PREFIX = 'custom/';
const CUSTOM_LABEL_VALUE_PATTERN = /^custom\/[0-9a-fA-F]{24}$/;

export const isCustomLabelValue = value =>
  typeof value === 'string' && CUSTOM_LABEL_VALUE_PATTERN.test(value);

export const toCustomLabelValue = labelId => `${CUSTOM_LABEL_PREFIX}${labelId}`;

/**
 * The team's labels (`GET /labels`, LabelDTO[]), kept here so plain helpers can name a
 * `custom/<labelId>` value. `useTeamLabels` fills it whenever the list loads.
 */
let customLabelsByValue = new Map();
let hasTeamLabels = false;

export function registerTeamLabels(labels) {
  hasTeamLabels = Array.isArray(labels);
  customLabelsByValue = new Map(
    (Array.isArray(labels) ? labels : [])
      .filter(label => label?.kind === 'custom' && isCustomLabelValue(label?.value))
      .map(label => [label.value, label])
  );
}

/** The team's label for a `custom/<labelId>` value, or undefined (not loaded yet, or deleted). */
export const getCustomLabel = value => customLabelsByValue.get(value);

/**
 * Keep only valid KA label values (the enum, or `custom/<labelId>`), deduped, for
 * `pageIdsWithLabels` / `documentIds[].labels`.
 */
export function sanitizeKaBucketPageLabels(labels) {
  if (!Array.isArray(labels)) return [];
  return [
    ...new Set(
      labels.filter(v => typeof v === 'string' && (LABEL_VALUE_SET.has(v) || isCustomLabelValue(v)))
    ),
  ];
}

/** Label values kept on items for the API but never shown in the UI. Currently none. */
export const KA_UI_HIDDEN_LABEL_VALUES = Object.freeze([]);

const KA_UI_HIDDEN_LABEL_VALUE_SET = new Set(KA_UI_HIDDEN_LABEL_VALUES);

export function isKaLabelHiddenInUi(value) {
  return KA_UI_HIDDEN_LABEL_VALUE_SET.has(value);
}

/** UI-facing label list — Knowledge Base first, then remaining labels in original order. */
export function getVisibleKaBucketLabels(labels) {
  const visible = sanitizeKaBucketPageLabels(labels).filter(value => !isKaLabelHiddenInUi(value));
  const knowledgeBase = KAActionBucketPageDataLabelValues.KnowledgeAgent;
  if (!visible.includes(knowledgeBase)) return visible;
  return [knowledgeBase, ...visible.filter(value => value !== knowledgeBase)];
}

/**
 * Normalized key so UI URLs and `/Page/addPageManuallyWithFile` response `page.url` match even when
 * scheme, `www.`, or trailing slashes differ.
 */
export function matchKeyForKaBucketManualPageUrl(u) {
  if (!u || typeof u !== 'string') return '';
  const raw = u.trim();
  if (!raw) return '';
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const { hostname, pathname, search, hash } = new URL(href);
    const host = hostname.replace(/^www\./i, '').toLowerCase();
    const path = `${pathname}${search}${hash}`.replace(/\/+$/, '') || '';
    return path ? `${host}${path}` : host;
  } catch {
    return raw
      .toLowerCase()
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '');
  }
}

const V = KAActionBucketPageDataLabelValues;

/** Legacy collection labels still returned by the API until items are re-indexed. */
const KA_LEGACY_LABEL_TO_DISPLAY = Object.freeze({
  [V.PersonalizationTool_Awards_AwardKeyDates]: 'Awards',
});

/** Same API value in every personalization group — one label on the collection, pickable from any category. */
export const KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS = Object.freeze([
  { label: 'Event info', value: V.PersonalizationTool_Events_EventInfo },
]);

const SHARED_LABEL_VALUE_SET = new Set(
  KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS.map(o => o.value)
);

/**
 * Grouped options for `Select` (`mode="multiple"`). Groups are UI-only; `value` is the API enum string.
 */
export const KA_ACTION_BUCKET_PAGE_LABEL_SELECT_GROUPS = [
  {
    label: 'Knowledge Base',
    options: [{ label: 'Knowledge Base', value: V.KnowledgeAgent }],
  },
  {
    label: 'Registrants',
    options: [
      { label: 'Sessions', value: V.PersonalizationTool_Registrant_Sessions },
      { label: 'Exhibitors', value: V.PersonalizationTool_Registrant_Exhibitors },
      { label: 'Passes', value: V.PersonalizationTool_Registrant_Passes },
      { label: 'Experiences', value: V.PersonalizationTool_Registrant_Experiences },
      { label: 'Speakers', value: V.PersonalizationTool_Registrant_Speakers },
      ...KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS,
    ],
  },
  {
    label: 'Sponsors',
    options: [
      { label: 'Sponsorship packages', value: V.PersonalizationTool_Sponsors_SponsorshipPackages },
      { label: 'Matched audiences', value: V.PersonalizationTool_Sponsors_MatchedAudiences },
      ...KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS,
    ],
  },
  {
    label: 'Awards',
    options: [
      { label: 'Award entrants / categories', value: V.PersonalizationTool_Awards_AwardEntrants },
      { label: 'Award judges', value: V.PersonalizationTool_Awards_AwardJudges },
      { label: 'Award key events', value: V.PersonalizationTool_Awards_AwardKeyEvents },
      { label: 'Award judging process', value: V.PersonalizationTool_Awards_AwardJudgeProcess },
      { label: 'Award previous winners', value: V.PersonalizationTool_Awards_AwardPreviousWinner },
      ...KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS,
    ],
  },
];

/** Label-picker groups shown as locked (not drillable) in KaBucketLabelsField. */
export const KA_ACTION_BUCKET_PAGE_LABEL_LOCKED_GROUP_LABELS = Object.freeze([]);

/** Flat `{ label, value }[]` (all leaves, deduped by value) */
export const KA_ACTION_BUCKET_PAGE_LABEL_OPTIONS = Object.freeze(
  KA_ACTION_BUCKET_PAGE_LABEL_SELECT_GROUPS.flatMap(g => g.options).filter((opt, idx, arr) => {
    return arr.findIndex(o => o.value === opt.value) === idx;
  })
);

export const KA_LABEL_DISPLAY_BY_VALUE = Object.freeze({
  ...Object.fromEntries(
    KA_ACTION_BUCKET_PAGE_LABEL_OPTIONS.map(({ value, label }) => [value, label])
  ),
  [V.KnowledgeAgent]: 'Knowledge Base',
  [V.PersonalizationTool_Awards_AwardKeyDates]: 'Award key events',
});

/** Ant Design Tag `color` per KA label value (display-only). */
export const KA_LABEL_TAG_COLOR_BY_VALUE = Object.freeze({
  [V.KnowledgeAgent]: 'blue',
  [V.PersonalizationTool_Registrant_Sessions]: 'cyan',
  [V.PersonalizationTool_Registrant_Exhibitors]: 'geekblue',
  [V.PersonalizationTool_Registrant_Passes]: 'purple',
  [V.PersonalizationTool_Registrant_Experiences]: 'magenta',
  [V.PersonalizationTool_Registrant_Speakers]: 'green',
  [V.PersonalizationTool_Events_EventInfo]: 'gold',
  [V.PersonalizationTool_Sponsors_SponsorshipPackages]: 'volcano',
  [V.PersonalizationTool_Sponsors_MatchedAudiences]: 'orange',
  [V.PersonalizationTool_Awards_AwardEntrants]: 'red',
  [V.PersonalizationTool_Awards_AwardJudges]: 'lime',
  [V.PersonalizationTool_Awards_AwardKeyEvents]: 'processing',
  [V.PersonalizationTool_Awards_AwardKeyDates]: 'processing',
  [V.PersonalizationTool_Awards_AwardJudgeProcess]: 'purple',
  [V.PersonalizationTool_Awards_AwardPreviousWinner]: 'magenta',
});

export function getKaLabelTagColor(value) {
  if (typeof value !== 'string' || !value) return 'default';
  // The team's own labels all share one hue, so they read as "yours" next to the defaults.
  if (isCustomLabelValue(value)) return 'purple';
  return KA_LABEL_TAG_COLOR_BY_VALUE[value] ?? 'default';
}

/** What a label value is called in the UI: the default's name, the team's label, or "Deleted label". */
export function getKaLabelDisplayName(value) {
  if (isCustomLabelValue(value)) {
    return getCustomLabel(value)?.name ?? (hasTeamLabels ? 'Deleted label' : 'Your label');
  }
  return KA_LABEL_DISPLAY_BY_VALUE[value] ?? value;
}

/**
 * `campaignData.personalizationTool.type` → `KA_ACTION_BUCKET_PAGE_LABEL_SELECT_GROUPS[].label`.
 * Align with PersonalizationType.jsx: `registrant`, `sponsor`, `award`.
 */
export const KA_PERSONALIZATION_TOOL_TYPE_TO_LABEL_GROUP_LABEL = Object.freeze({
  registrant: 'Registrants',
  sponsor: 'Sponsors',
  award: 'Awards',
});

/**
 * Label enum values required for collections when a personalization type is chosen.
 * @returns {{ groupTitle: string, labelValues: Set<string> } | null}
 */
export function getKaPersonalizationTypeLabelRequirement(type) {
  if (type == null || typeof type !== 'string') return null;
  const resolvedType = type === 'awardsEntrant' ? 'award' : type;
  const groupTitle = KA_PERSONALIZATION_TOOL_TYPE_TO_LABEL_GROUP_LABEL[resolvedType];
  if (!groupTitle) return null;
  const group = KA_ACTION_BUCKET_PAGE_LABEL_SELECT_GROUPS.find(g => g.label === groupTitle);
  if (!group?.options?.length) return null;
  return {
    groupTitle,
    labelValues: new Set(group.options.map(o => o.value)),
  };
}

/** True when one of the team's labels in `labels` is recommended to this personalisation type. */
export function hasCustomLabelForPersonalizationType(labels, type) {
  const resolvedType = type === 'awardsEntrant' ? 'award' : type;
  return (Array.isArray(labels) ? labels : []).some(
    value =>
      isCustomLabelValue(value) && (getCustomLabel(value)?.audiences ?? []).includes(resolvedType)
  );
}

/**
 * True when a collection with these labels can feed this personalisation type: a default label
 * of its category, or one of the team's labels recommended to it.
 */
export function collectionLabelsMatchPersonalizationType(labels, type) {
  const requirement = getKaPersonalizationTypeLabelRequirement(type);
  if (!requirement) return false;
  const list = Array.isArray(labels) ? labels : [];
  return (
    list.some(value => requirement.labelValues.has(value)) ||
    hasCustomLabelForPersonalizationType(list, type)
  );
}

/**
 * Sections for collection label popovers (API enum strings → grouped UI copy).
 * @returns {{ key: string, title: string, items: string[] }[]}
 */
export function buildKaBucketLabelsPopoverSections(labels) {
  const visibleLabels = getVisibleKaBucketLabels(labels);
  if (visibleLabels.length === 0) return [];

  const labelSet = new Set(visibleLabels);

  const knownValues = new Set(KA_ACTION_BUCKET_PAGE_LABEL_OPTIONS.map(o => o.value));

  const sections = [];

  const sharedMatched = KA_SHARED_ACTION_BUCKET_PAGE_LABEL_OPTIONS.filter(opt =>
    labelSet.has(opt.value)
  );
  if (sharedMatched.length > 0) {
    sections.push({
      key: 'shared',
      title: 'Shared',
      items: sharedMatched.map(opt => opt.label),
    });
  }

  for (const group of KA_ACTION_BUCKET_PAGE_LABEL_SELECT_GROUPS) {
    const matched = group.options.filter(
      opt => labelSet.has(opt.value) && !SHARED_LABEL_VALUE_SET.has(opt.value)
    );

    const legacyMatched = visibleLabels
      .filter(label => KA_LEGACY_LABEL_TO_DISPLAY[label] === group.label)
      .map(label => KA_LABEL_DISPLAY_BY_VALUE[label] ?? label);

    if (matched.length > 0 || legacyMatched.length > 0) {
      sections.push({
        key: group.label,
        title: group.label,
        items: [...matched.map(opt => opt.label), ...legacyMatched],
      });
    }
  }

  const customMatched = visibleLabels.filter(isCustomLabelValue);
  if (customMatched.length > 0) {
    sections.push({
      key: 'custom',
      title: 'Your labels',
      items: customMatched.map(getKaLabelDisplayName),
    });
  }

  const knownOrLegacyValues = new Set([...knownValues, ...Object.keys(KA_LEGACY_LABEL_TO_DISPLAY)]);
  const unknown = [
    ...new Set(visibleLabels.filter(l => !knownOrLegacyValues.has(l) && !isCustomLabelValue(l))),
  ];
  if (unknown.length > 0) {
    sections.push({
      key: 'other',
      title: 'Other',
      items: unknown.map(label => KA_LABEL_DISPLAY_BY_VALUE[label] ?? label),
    });
  }

  return sections;
}
